import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Archive, ArrowLeft, Download, History, Loader2, Plus, Minus, Pencil } from "lucide-react";
import { useState } from "react";
import { api, auditoriaArquivoUrl } from "../lib/api";

/**
 * Trilha de Auditoria (log de gravações) — SOMENTE o Dono.
 *
 * Mostra "quem alterou o quê e quando", com o detalhe campo a campo
 * (antes → depois). A trilha é preenchida automaticamente pelo backend
 * (listeners de eventos do SQLAlchemy); aqui só consultamos.
 */

interface Opcoes {
  tabelas: { valor: string; label: string }[];
  acoes: string[];
  condominios: { id: number; nome: string }[];
}

interface Mudanca {
  antes?: unknown;
  depois?: unknown;
}

interface LinhaAuditoria {
  id: number;
  em: string | null;
  usuario_nome: string | null;
  usuario_email: string | null;
  condominio_id: number | null;
  condominio_nome: string | null;
  tabela: string;
  tabela_label: string;
  registro_id: string | null;
  acao: string;
  origem: string | null;
  mudancas: Record<string, Mudanca>;
}

interface Pagina {
  total: number;
  pagina: number;
  por_pagina: number;
  paginas: number;
  itens: LinhaAuditoria[];
  refs?: { usuarios?: Record<string, string> };
}

interface ArquivoMorto {
  nome: string;
  bytes: number;
  mes: string;
}

function fmtTamanho(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

const ACAO_ROTULO: Record<string, string> = {
  criar: "Criou",
  editar: "Editou",
  excluir: "Excluiu",
  arquivar: "Arquivou",
};

function corAcao(acao: string): string {
  if (acao === "criar") return "text-emerald-700 bg-emerald-50 border-emerald-200";
  if (acao === "excluir") return "text-rose-700 bg-rose-50 border-rose-200";
  if (acao === "arquivar") return "text-slate-700 bg-slate-100 border-slate-200";
  return "text-amber-700 bg-amber-50 border-amber-200";
}

function IconeAcao({ acao }: { acao: string }) {
  if (acao === "criar") return <Plus size={14} />;
  if (acao === "excluir") return <Minus size={14} />;
  if (acao === "arquivar") return <Archive size={14} />;
  return <Pencil size={14} />;
}

function fmtDataHora(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString("pt-BR", {
    timeZone: "America/Sao_Paulo",  // sempre horário de Brasília (GMT-3)
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function fmtValor(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "boolean") return v ? "sim" : "não";
  if (typeof v === "object") return JSON.stringify(v);
  const s = String(v);
  return s.length > 120 ? s.slice(0, 120) + "…" : s;
}

export function AuditoriaPage({ onVoltar }: { onVoltar: () => void }) {
  const qc = useQueryClient();
  const [condominioId, setCondominioId] = useState<string>("");
  const [tabela, setTabela] = useState<string>("");
  const [acao, setAcao] = useState<string>("");
  const [q, setQ] = useState<string>("");
  const [qAtivo, setQAtivo] = useState<string>("");
  const [desde, setDesde] = useState<string>("");
  const [ate, setAte] = useState<string>("");
  const [pagina, setPagina] = useState<number>(1);
  const [mostrarArquivo, setMostrarArquivo] = useState<boolean>(false);

  const { data: opcoes } = useQuery<Opcoes>({
    queryKey: ["auditoria", "opcoes"],
    queryFn: () => api.get<Opcoes>("/auditoria/opcoes"),
  });

  const params = new URLSearchParams();
  if (condominioId) params.set("condominio_id", condominioId);
  if (tabela) params.set("tabela", tabela);
  if (acao) params.set("acao", acao);
  if (qAtivo) params.set("q", qAtivo);
  if (desde) params.set("desde", desde);
  if (ate) params.set("ate", ate);
  params.set("pagina", String(pagina));
  params.set("por_pagina", "50");

  // Atalho de período: preenche "de/até" com os últimos N dias.
  function ultimosDias(n: number) {
    const hoje = new Date();
    const ini = new Date();
    ini.setDate(hoje.getDate() - n);
    const iso = (d: Date) => d.toISOString().slice(0, 10);
    setDesde(iso(ini)); setAte(iso(hoje)); setPagina(1);
  }

  const { data, isLoading, isFetching } = useQuery<Pagina>({
    queryKey: ["auditoria", "lista", params.toString()],
    queryFn: () => api.get<Pagina>(`/auditoria?${params.toString()}`),
  });

  function limparFiltros() {
    setCondominioId(""); setTabela(""); setAcao(""); setQ(""); setQAtivo("");
    setDesde(""); setAte(""); setPagina(1);
  }

  // --- Arquivo morto (retenção) ---
  const { data: arquivos } = useQuery<{ itens: ArquivoMorto[]; retencao_meses: number }>({
    queryKey: ["auditoria", "arquivos"],
    queryFn: () => api.get<{ itens: ArquivoMorto[]; retencao_meses: number }>("/auditoria/arquivos"),
    enabled: mostrarArquivo,
  });
  const arquivar = useMutation({
    mutationFn: () => api.post<{ arquivados: number; arquivos: string[] }>("/auditoria/arquivar"),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["auditoria"] });
    },
  });
  const retencao = arquivos?.retencao_meses ?? 12;

  // Mostra o NOME ao lado de um usuario_id (ex.: "28 (teste do teste)").
  const nomesUsuarios = data?.refs?.usuarios ?? {};
  function refNome(campo: string, valor: unknown): string {
    if ((campo === "usuario_id" || campo.endsWith("_usuario_id")) && valor != null) {
      const nome = nomesUsuarios[String(valor)];
      return nome ? ` (${nome})` : "";
    }
    return "";
  }

  const selectCls =
    "text-sm rounded-md border border-slate-300 px-2.5 py-1.5 bg-white text-slate-700";

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
      <div className="flex items-center gap-3 mb-1">
        <button
          type="button"
          onClick={onVoltar}
          className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft size={16} /> Voltar
        </button>
      </div>

      <div className="flex items-center gap-2.5 mb-1">
        <div className="w-9 h-9 rounded-lg bg-slate-100 text-slate-700 flex items-center justify-center">
          <History size={20} />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Trilha de Auditoria</h2>
          <p className="text-xs text-slate-500">
            Registro automático de todas as gravações — quem alterou, quando e o que mudou.
          </p>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-2 mt-4 mb-4">
        <select
          value={condominioId}
          onChange={(e) => { setCondominioId(e.target.value); setPagina(1); }}
          className={selectCls}
        >
          <option value="">Todos os condomínios</option>
          {opcoes?.condominios.map((c) => (
            <option key={c.id} value={c.id}>{c.nome}</option>
          ))}
        </select>

        <select
          value={tabela}
          onChange={(e) => { setTabela(e.target.value); setPagina(1); }}
          className={selectCls}
        >
          <option value="">Tudo</option>
          {opcoes?.tabelas.map((t) => (
            <option key={t.valor} value={t.valor}>{t.label}</option>
          ))}
        </select>

        <select
          value={acao}
          onChange={(e) => { setAcao(e.target.value); setPagina(1); }}
          className={selectCls}
        >
          <option value="">Toda ação</option>
          {(opcoes?.acoes ?? []).map((a) => (
            <option key={a} value={a}>{ACAO_ROTULO[a] ?? a}</option>
          ))}
        </select>

        <form
          onSubmit={(e) => { e.preventDefault(); setQAtivo(q.trim()); setPagina(1); }}
          className="flex items-center gap-2"
        >
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por usuário ou rota…"
            className="text-sm rounded-md border border-slate-300 px-2.5 py-1.5 w-56"
          />
          <button
            type="submit"
            className="text-sm px-3 py-1.5 rounded-md border border-slate-300 bg-white hover:bg-slate-50"
          >
            Buscar
          </button>
        </form>

        <button
          type="button"
          onClick={limparFiltros}
          className="text-sm px-3 py-1.5 rounded-md text-slate-500 hover:text-slate-800"
        >
          Limpar
        </button>

        {isFetching && <Loader2 size={16} className="animate-spin text-slate-400" />}
      </div>

      {/* Filtro por período (data) */}
      <div className="flex flex-wrap items-center gap-2 mb-4 text-sm">
        <span className="text-slate-500">Período:</span>
        <input
          type="date"
          value={desde}
          onChange={(e) => { setDesde(e.target.value); setPagina(1); }}
          className={selectCls}
          aria-label="Data inicial"
        />
        <span className="text-slate-400">até</span>
        <input
          type="date"
          value={ate}
          onChange={(e) => { setAte(e.target.value); setPagina(1); }}
          className={selectCls}
          aria-label="Data final"
        />
        <button type="button" onClick={() => ultimosDias(7)}
          className="px-2.5 py-1.5 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50">
          7 dias
        </button>
        <button type="button" onClick={() => ultimosDias(30)}
          className="px-2.5 py-1.5 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50">
          30 dias
        </button>
        <button type="button" onClick={() => ultimosDias(90)}
          className="px-2.5 py-1.5 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50">
          90 dias
        </button>

        <button
          type="button"
          onClick={() => setMostrarArquivo((v) => !v)}
          className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
          title="Arquivo morto: exportar antigos para CSV e liberar espaço"
        >
          <Archive size={15} /> Arquivo morto
        </button>
      </div>

      {/* Arquivo morto (retenção) */}
      {mostrarArquivo && (
        <div className="mb-5 rounded-lg border border-slate-200 bg-slate-50 p-4">
          <div className="flex items-start gap-2">
            <Archive size={18} className="text-slate-600 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-slate-800">Arquivo morto (retenção de {retencao} meses)</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Para não pesar o banco, os registros com mais de {retencao} meses são
                exportados para uma planilha CSV (uma por mês, abre no Excel) e removidos
                do banco. <b>Nada se perde</b> — a planilha entra no seu backup. Roda
                sozinho 1× por mês; aqui você pode rodar na hora.
              </p>

              <div className="mt-3 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  disabled={arquivar.isPending}
                  onClick={() => {
                    if (confirm(`Exportar para CSV e apagar do banco os registros com mais de ${retencao} meses?`)) {
                      arquivar.mutate();
                    }
                  }}
                  className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-brand-700 text-white hover:brightness-110 disabled:opacity-50"
                >
                  {arquivar.isPending ? <Loader2 size={15} className="animate-spin" /> : <Archive size={15} />}
                  Arquivar agora (mais de {retencao} meses)
                </button>
                {arquivar.isSuccess && (
                  <span className="text-xs text-emerald-700">
                    {arquivar.data.arquivados === 0
                      ? "Nada para arquivar ainda."
                      : `${arquivar.data.arquivados} registro(s) arquivado(s).`}
                  </span>
                )}
                {arquivar.isError && (
                  <span className="text-xs text-rose-600">Não foi possível arquivar agora.</span>
                )}
              </div>

              {/* Lista de arquivos gerados */}
              <div className="mt-3">
                {(arquivos?.itens ?? []).length === 0 ? (
                  <p className="text-xs text-slate-400">Nenhum arquivo morto gerado ainda.</p>
                ) : (
                  <ul className="divide-y divide-slate-200 rounded-md border border-slate-200 bg-white">
                    {arquivos!.itens.map((a) => (
                      <li key={a.nome} className="flex items-center justify-between px-3 py-2 text-sm">
                        <span className="text-slate-700">{a.mes} <span className="text-slate-400">· {fmtTamanho(a.bytes)}</span></span>
                        <a
                          href={auditoriaArquivoUrl(a.nome)}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-brand-700 hover:underline"
                        >
                          <Download size={14} /> Baixar CSV
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lista */}
      {isLoading ? (
        <div className="flex items-center gap-2 text-slate-500 py-10 justify-center">
          <Loader2 size={18} className="animate-spin" /> Carregando…
        </div>
      ) : !data || data.itens.length === 0 ? (
        <div className="text-center text-slate-500 py-12 border border-dashed border-slate-200 rounded-lg">
          Nenhum registro de auditoria com esses filtros.
        </div>
      ) : (
        <>
          <p className="text-xs text-slate-500 mb-2">
            {data.total} registro(s). Página {data.pagina} de {data.paginas}.
          </p>
          <div className="space-y-2">
            {data.itens.map((ln) => (
              <div key={ln.id} className="border border-slate-200 rounded-lg p-3 bg-white">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-xs font-medium ${corAcao(ln.acao)}`}>
                    <IconeAcao acao={ln.acao} /> {ACAO_ROTULO[ln.acao] ?? ln.acao}
                  </span>
                  <span className="font-medium text-slate-800">{ln.tabela_label}</span>
                  {ln.registro_id && (
                    <span className="text-xs text-slate-400">#{ln.registro_id}</span>
                  )}
                  {ln.condominio_nome && (
                    <span className="text-xs text-slate-500">· {ln.condominio_nome}</span>
                  )}
                  <span className="text-xs text-slate-400 ml-auto">{fmtDataHora(ln.em)}</span>
                </div>

                <div className="text-xs text-slate-500 mt-1">
                  por <span className="font-medium text-slate-700">{ln.usuario_nome ?? "sistema"}</span>
                  {ln.usuario_email && <span> ({ln.usuario_email})</span>}
                </div>

                {/* Mudanças campo a campo */}
                {Object.keys(ln.mudancas || {}).length > 0 && (
                  <div className="mt-2 border-t border-slate-100 pt-2 space-y-1">
                    {Object.entries(ln.mudancas).map(([campo, m]) => (
                      <div key={campo} className="text-xs flex flex-wrap items-baseline gap-1.5">
                        <span className="font-medium text-slate-600">{campo}:</span>
                        {ln.acao !== "criar" && (
                          <span className="text-rose-600 line-through decoration-rose-300">
                            {fmtValor(m.antes)}{refNome(campo, m.antes)}
                          </span>
                        )}
                        {ln.acao !== "excluir" && (
                          <>
                            {ln.acao !== "criar" && <span className="text-slate-400">→</span>}
                            <span className="text-emerald-700">{fmtValor(m.depois)}{refNome(campo, m.depois)}</span>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Paginação */}
          {data.paginas > 1 && (
            <div className="flex items-center justify-center gap-3 mt-4">
              <button
                type="button"
                disabled={pagina <= 1}
                onClick={() => setPagina((p) => Math.max(1, p - 1))}
                className="text-sm px-3 py-1.5 rounded-md border border-slate-300 bg-white disabled:opacity-40"
              >
                Anterior
              </button>
              <span className="text-xs text-slate-500">{pagina} / {data.paginas}</span>
              <button
                type="button"
                disabled={pagina >= data.paginas}
                onClick={() => setPagina((p) => Math.min(data.paginas, p + 1))}
                className="text-sm px-3 py-1.5 rounded-md border border-slate-300 bg-white disabled:opacity-40"
              >
                Próxima
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
